import React from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { NumberCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer/NumberCellRenderer'
import CustomTooltip from '../../../components/tooltips/Tooltip/CustomTooltip'
import {
  getVarianceInfo,
  getVarianceTargetRow,
  getTaskVarianceInfo,
  getTotalTaskVarianceInfo,
  getTaskTotalVsSprintVarianceInfo,
  getSprintEstimatedHourList,
  getSprintTaskEstimatedHourList,
  SprintEstimatedHourInfo,
} from '../gridOptions/varianceUtils'
import { intl } from '../../../../i18n'
import Workload, {
  getWorkloadUnitValue,
  WorkloadUnit,
} from '../../../../lib/functions/workload'

// 工数単位のラベルを取得
const getWorkloadUnitLabel = (workloadUnit: WorkloadUnit): string => {
  switch (workloadUnit) {
    case WorkloadUnit.MONTH:
      return intl.formatMessage({ id: 'workloadUnit.month' })
    case WorkloadUnit.DAY:
      return intl.formatMessage({ id: 'workloadUnit.day' })
    case WorkloadUnit.HOUR:
    default:
      return intl.formatMessage({ id: 'workloadUnit.hour' })
  }
}

// 時間を選択中の単位に変換
const convertHourToUnit = (
  hour: number,
  workloadUnit: WorkloadUnit,
  workloadStandard: { dailyWorkHours: number; monthlyWorkDays: number }
): number => {
  const workload = Workload.from({
    hour,
    standard: workloadStandard,
  })
  return getWorkloadUnitValue(workload, workloadUnit)
}

// contextから工数単位の設定を取得するヘルパー
const getWorkloadSettings = (context: ICellRendererParams['context']) => {
  const workloadUnit =
    context?.workloadUnitState?.selectedWorkloadUnit || WorkloadUnit.HOUR
  const workloadStandard = context?.workloadUnitState || {
    dailyWorkHours: 1,
    monthlyWorkDays: 1,
  }
  const unitLabel = getWorkloadUnitLabel(workloadUnit)
  return { workloadUnit, workloadStandard, unitLabel }
}

// 値行（ラベル + 値）
type ValueLine = {
  label: string
  hour: number
  indent: number // インデントレベル（0: 通常, 1: 内訳）
}

// 乖離ブロック（メッセージ + 値のセット）
type VarianceBlock = {
  message: string
  values: ValueLine[]
}

// スプリント内訳の値行を生成
const buildSprintBreakdownLines = (
  sprintBreakdown: SprintEstimatedHourInfo[]
): ValueLine[] =>
  sprintBreakdown.map(sprint => ({
    label: `${sprint.sprintName}：`,
    hour: sprint.estimatedHour,
    indent: 1,
  }))

// 乖離ブロックのリストからツールチップテキストを生成（パターン2形式）
const buildTooltipFromBlocks = (
  blocks: VarianceBlock[],
  workloadUnit: WorkloadUnit,
  workloadStandard: { dailyWorkHours: number; monthlyWorkDays: number },
  unitLabel: string
): string => {
  return blocks
    .map(block => {
      const valueLines = block.values
        .map(v => {
          const value = convertHourToUnit(
            v.hour,
            workloadUnit,
            workloadStandard
          )
          const prefix = v.indent === 0 ? '  ' : '    '
          return `${prefix}${v.label}${value}${unitLabel}`
        })
        .join('\n')
      return `${block.message}\n${valueLines}`
    })
    .join('\n\n')
}

// ============================================================================
// 各列用のツールチップビルダー
// ============================================================================

type VarianceRow = NonNullable<ReturnType<typeof getVarianceTargetRow>>

// 成果物列用: チェック1（成果物 vs 成果物スプリント合計）+ チェック2（成果物 vs タスク）
const buildDeliverableTotalTooltip = (
  row: VarianceRow,
  context: ICellRendererParams['context']
): string => {
  const { workloadUnit, workloadStandard, unitLabel } =
    getWorkloadSettings(context)

  const blocks: VarianceBlock[] = []

  const varianceInfo = getVarianceInfo(row)
  if (varianceInfo.hasVariance) {
    const sprintBreakdown = getSprintEstimatedHourList(row)
    blocks.push({
      message: intl.formatMessage({
        id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableVsSprint',
      }),
      values: [
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableLabel',
          }),
          hour: varianceInfo.originalHour,
          indent: 0,
        },
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableSprintLabel',
          }),
          hour: varianceInfo.sprintTotalHour,
          indent: 0,
        },
        ...buildSprintBreakdownLines(sprintBreakdown),
      ],
    })
  }

  const totalTaskInfo = getTotalTaskVarianceInfo(row)
  if (totalTaskInfo.hasVariance) {
    blocks.push({
      message: intl.formatMessage({
        id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableVsTask',
      }),
      values: [
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableLabel',
          }),
          hour: totalTaskInfo.deliverableHour,
          indent: 0,
        },
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.taskLabel',
          }),
          hour: totalTaskInfo.taskHour,
          indent: 0,
        },
      ],
    })
  }

  return buildTooltipFromBlocks(
    blocks,
    workloadUnit,
    workloadStandard,
    unitLabel
  )
}

// 成果物（スプリント）列用: チェック1（成果物 vs 成果物スプリント合計）+ チェック4（成果物スプリント vs タスクスプリント）
const buildDeliverableSprintTooltip = (
  row: VarianceRow,
  context: ICellRendererParams['context']
): string => {
  const { workloadUnit, workloadStandard, unitLabel } =
    getWorkloadSettings(context)

  const blocks: VarianceBlock[] = []

  const varianceInfo = getVarianceInfo(row)
  if (varianceInfo.hasVariance) {
    const sprintBreakdown = getSprintEstimatedHourList(row)
    blocks.push({
      message: intl.formatMessage({
        id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableVsSprint',
      }),
      values: [
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableLabel',
          }),
          hour: varianceInfo.originalHour,
          indent: 0,
        },
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableSprintLabel',
          }),
          hour: varianceInfo.sprintTotalHour,
          indent: 0,
        },
        ...buildSprintBreakdownLines(sprintBreakdown),
      ],
    })
  }

  const taskVarianceInfo = getTaskVarianceInfo(row)
  if (taskVarianceInfo.hasVariance) {
    blocks.push({
      message: intl.formatMessage({
        id: 'productBacklog.estimatedWorkload.variance.tooltip.sprintVsTaskSprint',
      }),
      values: [
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableSprintLabel',
          }),
          hour: taskVarianceInfo.deliverableHour,
          indent: 0,
        },
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.taskSprintLabel',
          }),
          hour: taskVarianceInfo.taskHour,
          indent: 0,
        },
      ],
    })
  }

  return buildTooltipFromBlocks(
    blocks,
    workloadUnit,
    workloadStandard,
    unitLabel
  )
}

// タスク列用: チェック2（成果物 vs タスク）+ チェック3（タスク vs タスクスプリント合計）
const buildTaskTotalTooltip = (
  row: VarianceRow,
  context: ICellRendererParams['context']
): string => {
  const { workloadUnit, workloadStandard, unitLabel } =
    getWorkloadSettings(context)

  const blocks: VarianceBlock[] = []

  const totalTaskInfo = getTotalTaskVarianceInfo(row)
  if (totalTaskInfo.hasVariance) {
    blocks.push({
      message: intl.formatMessage({
        id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableVsTask',
      }),
      values: [
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableLabel',
          }),
          hour: totalTaskInfo.deliverableHour,
          indent: 0,
        },
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.taskLabel',
          }),
          hour: totalTaskInfo.taskHour,
          indent: 0,
        },
      ],
    })
  }

  const taskSprintInfo = getTaskTotalVsSprintVarianceInfo(row)
  if (taskSprintInfo.hasVariance) {
    const sprintBreakdown = getSprintTaskEstimatedHourList(row)
    blocks.push({
      message: intl.formatMessage({
        id: 'productBacklog.estimatedWorkload.variance.tooltip.taskVsTaskSprint',
      }),
      values: [
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.taskLabel',
          }),
          hour: taskSprintInfo.taskHour,
          indent: 0,
        },
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.taskSprintLabel',
          }),
          hour: taskSprintInfo.sprintTotalHour,
          indent: 0,
        },
        ...buildSprintBreakdownLines(sprintBreakdown),
      ],
    })
  }

  return buildTooltipFromBlocks(
    blocks,
    workloadUnit,
    workloadStandard,
    unitLabel
  )
}

// タスク（スプリント）列用: チェック3（タスク vs タスクスプリント合計）+ チェック4（成果物スプリント vs タスクスプリント）
const buildTaskSprintTooltip = (
  row: VarianceRow,
  context: ICellRendererParams['context']
): string => {
  const { workloadUnit, workloadStandard, unitLabel } =
    getWorkloadSettings(context)

  const blocks: VarianceBlock[] = []

  const taskSprintInfo = getTaskTotalVsSprintVarianceInfo(row)
  if (taskSprintInfo.hasVariance) {
    const sprintBreakdown = getSprintTaskEstimatedHourList(row)
    blocks.push({
      message: intl.formatMessage({
        id: 'productBacklog.estimatedWorkload.variance.tooltip.taskVsTaskSprint',
      }),
      values: [
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.taskLabel',
          }),
          hour: taskSprintInfo.taskHour,
          indent: 0,
        },
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.taskSprintLabel',
          }),
          hour: taskSprintInfo.sprintTotalHour,
          indent: 0,
        },
        ...buildSprintBreakdownLines(sprintBreakdown),
      ],
    })
  }

  const taskVarianceInfo = getTaskVarianceInfo(row)
  if (taskVarianceInfo.hasVariance) {
    blocks.push({
      message: intl.formatMessage({
        id: 'productBacklog.estimatedWorkload.variance.tooltip.sprintVsTaskSprint',
      }),
      values: [
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableSprintLabel',
          }),
          hour: taskVarianceInfo.deliverableHour,
          indent: 0,
        },
        {
          label: intl.formatMessage({
            id: 'productBacklog.estimatedWorkload.variance.tooltip.taskSprintLabel',
          }),
          hour: taskVarianceInfo.taskHour,
          indent: 0,
        },
      ],
    })
  }

  return buildTooltipFromBlocks(
    blocks,
    workloadUnit,
    workloadStandard,
    unitLabel
  )
}

// ============================================================================
// 乖離セルレンダラー
// ============================================================================

const createVarianceRenderer =
  (
    checkVariance: (row: VarianceRow) => boolean,
    buildTooltip: (
      row: VarianceRow,
      context: ICellRendererParams['context']
    ) => string
  ): React.FC<ICellRendererParams> =>
  props => {
    const row = getVarianceTargetRow(props.data)
    const isHighlighted = row ? checkVariance(row) : false
    const tooltipValue =
      isHighlighted && row ? buildTooltip(row, props.context) : ''

    if (!tooltipValue) {
      return <NumberCellRenderer {...props} />
    }
    return (
      <CustomTooltip value={tooltipValue}>
        <NumberCellRenderer {...props} />
      </CustomTooltip>
    )
  }

// 成果物列用: チェック1 or チェック2
export const DeliverableTotalCellRenderer = createVarianceRenderer(
  row =>
    getVarianceInfo(row).hasVariance ||
    getTotalTaskVarianceInfo(row).hasVariance,
  buildDeliverableTotalTooltip
)

// 成果物（スプリント）列用: チェック1 or チェック4
const EstimatedWorkloadCellRenderer = createVarianceRenderer(
  row =>
    getVarianceInfo(row).hasVariance || getTaskVarianceInfo(row).hasVariance,
  buildDeliverableSprintTooltip
)

// タスク列用: チェック2 or チェック3
export const TotalTaskEstimatedWorkloadCellRenderer = createVarianceRenderer(
  row =>
    getTotalTaskVarianceInfo(row).hasVariance ||
    getTaskTotalVsSprintVarianceInfo(row).hasVariance,
  buildTaskTotalTooltip
)

// タスク（スプリント）列用: チェック3 or チェック4
export const TaskEstimatedWorkloadCellRenderer = createVarianceRenderer(
  row =>
    getTaskTotalVsSprintVarianceInfo(row).hasVariance ||
    getTaskVarianceInfo(row).hasVariance,
  buildTaskSprintTooltip
)

export default EstimatedWorkloadCellRenderer
